import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../i18n";
import { GALLERY } from "../siteData";

const CATS = ["all", "Haircut", "Beard & Shave", "Executive", "Coloring"];

export function Gallery() {
    const { lang, t } = useLang();
    const [cat, setCat] = useState("all");
    const [active, setActive] = useState(null);
    const items = cat === "all" ? GALLERY : GALLERY.filter((g) => g.category === cat);

    return (
        <section id="gallery" className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6" data-testid="gallery-section">
            <motion.div
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="flex flex-wrap items-end justify-between gap-6"
            >
                <div>
                    <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#FFB800]">{t.gallery.overline}</p>
                    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                        {t.gallery.title}
                    </h2>
                    <p className="mt-3 text-[#8E8EA0]">{t.gallery.sub}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {CATS.map((c) => (
                        <button
                            key={c}
                            data-testid="gallery-filter-btn"
                            onClick={() => setCat(c)}
                            className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-300 ${
                                cat === c
                                    ? "border-[#FFB800] bg-[#FFB800] text-[#0B0B0E] font-semibold"
                                    : "border-white/15 text-[#8E8EA0] hover:border-[#FFB800]/60 hover:text-[#FFB800]"
                            }`}
                        >
                            {t.gallery.filters[c]}
                        </button>
                    ))}
                </div>
            </motion.div>

            <motion.div layout className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence mode="popLayout">
                    {items.map((g) => (
                        <motion.button
                            layout
                            key={g.id}
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            transition={{ duration: 0.45 }}
                            onClick={() => setActive(g)}
                            className="group relative overflow-hidden rounded-3xl border border-white/10 text-left"
                        >
                            <img
                                src={g.url}
                                alt={lang === "zh" ? g.title_zh : g.title_en}
                                loading="lazy"
                                className="h-80 w-full object-cover transition-transform duration-700 group-hover:scale-108"
                                style={{ transform: "scale(1.001)" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E]/95 via-transparent to-transparent opacity-90" />
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                    {g.tags.map((tag) => (
                                        <span key={tag} className="rounded-full bg-[#FFB800]/15 px-2.5 py-0.5 font-mono2 text-[10px] uppercase tracking-wider text-[#FFB800]">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <p className="font-display text-lg font-semibold text-white">{lang === "zh" ? g.title_zh : g.title_en}</p>
                            </div>
                        </motion.button>
                    ))}
                </AnimatePresence>
            </motion.div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
                        onClick={() => setActive(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 24 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 24 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="relative max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-[#FFB800]/30"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img src={active.url} alt="" className="max-h-[85vh] w-full object-cover" />
                            <button
                                onClick={() => setActive(null)}
                                className="absolute right-4 top-4 rounded-full bg-black/70 p-2 text-white hover:bg-[#FF2E4D] transition-colors"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                                <p className="font-display text-xl font-bold text-white">{lang === "zh" ? active.title_zh : active.title_en}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
